import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { createBatchConfig } from "data/mutations-common";
import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { BatchConfig } from "../models";
import { HistoryState } from "../types";
import { batchOutputGeocode } from "../utils";
import { DefineBatchJob } from "./step-define-batch-job/define-batch-job";
import { DefineOutputFileMappings } from "./step-define-output-file-mappings/define-output-file-mappings-page";
import { ViewSampleBatchJob } from "./step-preview-sample/view-sample-batch-job";
import { StartBatchJob } from "./step-start-batch-job/start-batch-job";
import { UploadInputFile } from "./step-upload-input-file/upload-input-file-page";

export const CreateBatchPage = () => {
  const history = useHistory();
  const { batchId, seedBatchConfig } = history.location.state as HistoryState;
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const queryClient = useQueryClient();

  var initialBatchConfig: BatchConfig = {
    displayName: "",
    type: "addressGeocoder",
    batchInfo: {
      batchId: batchId,
    },
    parameters: {
      matchType: "",
      stateFilter: [],
      dataset: [],
      additionalProperties: [],
    },
    hubInfo: {
      activeStep: 0,
      csvHeaders: [],
      files: [],
      batchConfigHash: "",
      checkedItems: batchOutputGeocode.properties,

      headersMessageAck: false,
      sample: {
        rows: [],
        request: [],
        response: [],
      },
    },
    input: {
      format: [],
      file: "",
    },
    output: {
      format: [],
    },
    returnDownloadLink: true,
    status: "Draft",
    isLoading: false,
  };

  const [batchConfig, setBatchConfig] = useState<BatchConfig>(
    seedBatchConfig ? seedBatchConfig : initialBatchConfig,
  );

  if (import.meta.env.VITE_ENVIRONMENT !== "production") {
    console.log("batch config", batchConfig);
  }

  const [activeStep, setActiveStep] = useState(batchConfig.hubInfo.activeStep);

  if (activeStep != batchConfig.hubInfo.activeStep) {
    setBatchConfig((prev) => ({
      ...prev,
      hubInfo: {
        ...prev.hubInfo,
        activeStep: activeStep,
      },
      status: activeStep != 4 ? "Draft" : prev.status,
    }));
  }

  const batchesCreateMutation = useMutation({
    mutationFn: (values: any) => createBatchConfig(values.batchConfig),
    onSuccess: (_, variables) => {
      if (variables.navigate) {
        queryClient.removeQueries({
          queryKey: [QueryKeys.batchesKey, identityId],
        });
        history.push(Routes.batchBatches);
      }

      if (variables.nextStep) {
        setActiveStep(variables.nextStep);
      }
    },
  });

  const stepsMapping = [
    {
      stepName: "Job",
      element: DefineBatchJob,
    },
    {
      stepName: "Input",
      element: UploadInputFile,
    },
    {
      stepName: "Sample",
      element: ViewSampleBatchJob,
    },
    {
      stepName: "Output",
      element: DefineOutputFileMappings,
    },
    {
      stepName: "Start",
      element: StartBatchJob,
    },
  ];

  return (
    <div className="relative">
      <div className="absolute -top-14 right-0 flex items-center justify-end">
        <Button
          onClick={async () => {
            batchesCreateMutation.mutate({
              batchConfig: batchConfig,
              navigate: true,
            });
          }}
          disabled={batchesCreateMutation.isPending}
        >
          {batchesCreateMutation.isPending && <Spinner />}
          Save and Exit
        </Button>
      </div>

      <ReactQueryErrorWrapper
        queries={[]}
        mutations={[batchesCreateMutation]}
      />

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between w-full">
          {stepsMapping.map((step, index) => (
            <div
              key={step.stepName}
              className="flex items-center flex-1 last:flex-none"
            >
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "flex items-center justify-center size-7 rounded-full text-xs font-medium shrink-0",
                    index <= activeStep
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {index < activeStep ? (
                    <CheckCircle className="size-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={cn(
                    "text-xs",
                    index <= activeStep
                      ? "font-medium"
                      : "text-muted-foreground",
                  )}
                >
                  {step.stepName}
                </span>
              </div>
              {index < stepsMapping.length - 1 && (
                <div
                  className={cn(
                    "h-px flex-1 mx-3",
                    index < activeStep ? "bg-primary" : "bg-muted",
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {React.createElement(stepsMapping[activeStep].element, {
          activeStep: activeStep,
          setActiveStep: setActiveStep,
          maxSteps: stepsMapping.length - 1,
          batchConfig: batchConfig,
          setBatchConfig: setBatchConfig,
          saveBatchConfig: async (config?: BatchConfig, nextStep?: number) => {
            const saveConfig = config ?? batchConfig;

            return await batchesCreateMutation.mutateAsync({
              batchConfig: saveConfig,
              navigate: false,
              nextStep: nextStep,
            });
          },
          isSaveBatchConfigLoading: batchesCreateMutation.isPending,
        })}
      </div>
    </div>
  );
};
